/**
 * Claim-workflow hook surface - kept stable for the claim workflows admin
 * UI while the data source migrates to the generic apps.workflows engine.
 *
 * Phase 1 of the tenant-agnostic claims migration deleted the
 * /api/claims/claim-workflows/* surface. These hooks now translate the
 * legacy claim-workflows shape to/from
 * /api/workflows/admin/definitions/* on the wire, and reshape responses
 * so the admin pages keep rendering without UI changes.
 *
 * Notable shape adapters:
 *   - generic `label` ↔ old `name`
 *   - generic `current_version.{states, actions}` flattened onto the
 *     workflow detail
 *   - per-state `role_label` and `editable_fields` are deleted columns;
 *     they are stubbed as empty so the legacy UI rows render harmlessly
 *   - per-action `prerequisites` and `capture_fields` are deleted
 *     columns; legacy `condition` JSON is exposed as `prerequisites`
 *     and `capture_fields` is stubbed empty
 *   - Fault definitions are gone - Phase 3 rebuilds as a Tier 2
 *     `fault` entity. The hooks return [] / no-op mutations.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import api from '@/admin/lib/api'

// ── Types ────────────────────────────────────────────────

export interface WorkflowState {
  uuid: string
  key: string
  label: string
  color: string
  role_label: string
  is_terminal: boolean
  is_initial: boolean
  editable_fields: string[]
  order: number
  // Whether this state belongs on the linear progress stepper. The
  // backend derives it from the action graph: side exits (park, hold,
  // bulk-settle - reachable from any state), states nothing can reach,
  // and branch outcomes such as Denied are all excluded, while the one
  // closure reached from the final stage is included. A state that only
  // loops back to an earlier step is NOT excluded - a claim genuinely
  // sits in Awaiting Info. Optional so payloads that predate it keep
  // the previous terminal-only rule.
  is_linear_stage?: boolean
  attribute_values?: Record<string, unknown>
}

export interface WorkflowActionDef {
  uuid: string
  key: string
  label: string
  icon: string
  applicability: 'CREATION' | 'STATE' | 'GLOBAL'
  actionable: boolean
  source_state_uuid: string | null
  source_state_key: string | null
  target_state_uuid: string | null
  target_state_key: string | null
  order: number
  auto_proceed: boolean
  prerequisites: Record<string, unknown> | null
  side_effects: Record<string, unknown> | null
  capture_fields: CaptureFieldEntry[] | null
  consumer_capture_fields: CaptureFieldEntry[] | null
  actor: 'ADMIN' | 'CONSUMER'
}

export interface ClaimWorkflowRow {
  uuid: string
  name: string
  description: string
  claim_schema_uuid: string | null
  claim_schema_name: string | null
  created_at: string
  updated_at: string
}

export interface ClaimWorkflowDetail extends ClaimWorkflowRow {
  states: WorkflowState[]
  actions: WorkflowActionDef[]
}

// ── Generic-engine response shapes (subset) ──────────────

interface GenericWorkflow {
  uuid: string
  key: string
  label: string
  description: string
  resource_type: string
  is_active: boolean
  metadata: Record<string, unknown>
  current_version?: GenericWorkflowVersion | null
}

interface GenericWorkflowVersion {
  uuid: string
  version_number: number
  is_current: boolean
  published_at: string | null
  states: GenericWorkflowState[]
  actions: GenericWorkflowAction[]
}

interface GenericWorkflowState {
  uuid: string
  key: string
  label: string
  is_terminal: boolean
  is_initial: boolean
  color: string
  order: number
  side_effects?: unknown[]
}

interface GenericWorkflowAction {
  uuid: string
  key: string
  label: string
  icon: string
  applicability: 'CREATION' | 'STATE' | 'GLOBAL'
  actor: 'ADMIN' | 'CONSUMER' | 'SYSTEM'
  order: number
  source_state_uuid: string | null
  source_state_key: string | null
  target_state_uuid: string | null
  target_state_key: string | null
  condition?: Record<string, unknown> | null
  side_effects?: unknown[]
  ui_hints?: Record<string, unknown> | null
}

// ── Shape adapters ───────────────────────────────────────

/**
 * A single captured-field entry in a CREATION action's registration
 * config. Entries are stored on ``ui_hints.capture_fields`` (admin) and
 * ``ui_hints.consumer_capture_fields`` (consumer). ``label`` is a display
 * override stored in metadata only - it never mutates the ClaimAttribute.
 */
export interface CaptureFieldEntry {
  code_name: string
  label?: string | null
  required?: boolean | null
}

/** Normalize a raw capture list (legacy bare strings or structured
 * objects) into a uniform ``CaptureFieldEntry[]``. */
export function normaliseCaptureEntries(raw: unknown): CaptureFieldEntry[] {
  if (!Array.isArray(raw)) return []
  const entries: CaptureFieldEntry[] = []
  for (const item of raw) {
    if (typeof item === 'string') {
      const code = item.trim()
      if (code) entries.push({ code_name: code })
    } else if (item && typeof item === 'object') {
      const obj = item as Record<string, unknown>
      const code = typeof obj.code_name === 'string' ? obj.code_name.trim() : ''
      if (!code) continue
      entries.push({
        code_name: code,
        label: typeof obj.label === 'string' ? obj.label : null,
        required: typeof obj.required === 'boolean' ? obj.required : null,
      })
    }
  }
  return entries
}

function adaptWorkflowRow(wf: GenericWorkflow): ClaimWorkflowRow {
  const meta = wf.metadata ?? {}
  return {
    uuid: wf.uuid,
    name: wf.label,
    description: wf.description ?? '',
    claim_schema_uuid: (meta.claim_schema_id as string | undefined) ?? null,
    claim_schema_name: null,
    created_at: '',
    updated_at: '',
  }
}

function adaptState(s: GenericWorkflowState): WorkflowState {
  return {
    uuid: s.uuid,
    key: s.key,
    label: s.label,
    color: s.color,
    role_label: '',
    is_terminal: s.is_terminal,
    is_initial: s.is_initial,
    editable_fields: [],
    order: s.order,
  }
}

function adaptAction(a: GenericWorkflowAction): WorkflowActionDef {
  return {
    uuid: a.uuid,
    key: a.key,
    label: a.label,
    icon: a.icon ?? '',
    applicability: a.applicability,
    actionable: false,
    source_state_uuid: a.source_state_uuid ?? null,
    source_state_key: a.source_state_key ?? null,
    target_state_uuid: a.target_state_uuid ?? null,
    target_state_key: a.target_state_key ?? null,
    order: a.order,
    auto_proceed: false,
    prerequisites: (a.condition as Record<string, unknown> | null) ?? null,
    side_effects: Array.isArray(a.side_effects)
      ? (a.side_effects as unknown as Record<string, unknown>)
      : null,
    capture_fields: normaliseCaptureEntries(a.ui_hints?.capture_fields),
    consumer_capture_fields: normaliseCaptureEntries(
      a.ui_hints?.consumer_capture_fields,
    ),
    actor: a.actor === 'SYSTEM' ? 'ADMIN' : a.actor,
  }
}

function adaptDetail(wf: GenericWorkflow): ClaimWorkflowDetail {
  const row = adaptWorkflowRow(wf)
  const version = wf.current_version ?? null
  return {
    ...row,
    states: (version?.states ?? []).map(adaptState),
    actions: (version?.actions ?? []).map(adaptAction),
  }
}

// ── Workflow Hooks ───────────────────────────────────────

export function useClaimWorkflows() {
  return useQuery<ClaimWorkflowRow[]>({
    queryKey: ['claim-workflows'],
    queryFn: () =>
      api
        .get<GenericWorkflow[]>('/workflows/admin/definitions/', {
          params: { resource_type: 'claim' },
        })
        .then((r) => (r.data ?? []).map(adaptWorkflowRow)),
  })
}

export function useClaimWorkflow(uuid: string | null) {
  return useQuery<ClaimWorkflowDetail>({
    queryKey: ['claim-workflow', uuid],
    queryFn: () =>
      api
        .get<GenericWorkflow>(`/workflows/admin/definitions/${uuid}/`)
        .then((r) => adaptDetail(r.data)),
    enabled: !!uuid,
  })
}

function slugifyKey(label: string): string {
  return label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
}

export function useCreateClaimWorkflow() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: {
      name: string
      description?: string
      claim_schema_uuid?: string | null
    }) => {
      const key = slugifyKey(data.name)
      const metadata: Record<string, unknown> = {}
      if (data.claim_schema_uuid) metadata.claim_schema_id = data.claim_schema_uuid
      return api
        .post('/workflows/admin/definitions/', {
          resource_type: 'claim',
          key,
          label: data.name,
          description: data.description ?? '',
          metadata,
        })
        .then((r) => adaptWorkflowRow(r.data))
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['claim-workflows'] }),
  })
}

export function useUpdateClaimWorkflow() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ uuid, data }: { uuid: string; data: Record<string, unknown> }) => {
      const payload: Record<string, unknown> = {}
      if ('name' in data) payload.label = data.name
      if ('description' in data) payload.description = data.description
      if ('is_active' in data) payload.is_active = data.is_active
      return api
        .patch(`/workflows/admin/definitions/${uuid}/`, payload)
        .then((r) => adaptWorkflowRow(r.data))
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['claim-workflows'] })
      qc.invalidateQueries({ queryKey: ['claim-workflow', vars.uuid] })
    },
  })
}

export function useDeleteClaimWorkflow() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (uuid: string) =>
      api.delete(`/workflows/admin/definitions/${uuid}/`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['claim-workflows'] }),
  })
}

// ── State Hooks ─────────────────────────────────────────

export function useCreateWorkflowState() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      workflowUuid,
      data,
    }: {
      workflowUuid: string
      data: Partial<WorkflowState> & { key: string; label: string }
    }) =>
      api
        .post(`/workflows/admin/definitions/${workflowUuid}/states/`, {
          key: data.key,
          label: data.label,
          color: data.color ?? '#E5E7EB',
          is_initial: data.is_initial ?? false,
          is_terminal: data.is_terminal ?? false,
          order: data.order ?? 0,
        })
        .then((r) => r.data),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['claim-workflows'] })
      qc.invalidateQueries({ queryKey: ['claim-workflow', vars.workflowUuid] })
    },
  })
}

export function useUpdateWorkflowState() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: {
      workflowUuid: string
      stateUuid: string
      data: Partial<WorkflowState>
    }) => {
      const { stateUuid, data } = vars
      const payload: Record<string, unknown> = {}
      if ('label' in data) payload.label = data.label
      if ('color' in data) payload.color = data.color
      if ('is_initial' in data) payload.is_initial = data.is_initial
      if ('is_terminal' in data) payload.is_terminal = data.is_terminal
      if ('order' in data) payload.order = data.order
      return api
        .patch(`/workflows/admin/states/${stateUuid}/`, payload)
        .then((r) => r.data)
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['claim-workflows'] })
      qc.invalidateQueries({ queryKey: ['claim-workflow', vars.workflowUuid] })
    },
  })
}

export function useDeleteWorkflowState() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: { workflowUuid: string; stateUuid: string }) =>
      api.delete(`/workflows/admin/states/${vars.stateUuid}/`),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['claim-workflows'] })
      qc.invalidateQueries({ queryKey: ['claim-workflow', vars.workflowUuid] })
    },
  })
}

// ── Action Hooks ────────────────────────────────────────

export function useCreateWorkflowAction() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      workflowUuid,
      data,
    }: {
      workflowUuid: string
      data: Record<string, unknown>
    }) => {
      const payload: Record<string, unknown> = {
        key: data.key,
        label: data.label,
        icon: data.icon ?? '',
        applicability: data.applicability ?? 'STATE',
        actor: data.actor ?? 'ADMIN',
        order: data.order ?? 0,
      }
      if (data.source_state_uuid) payload.source_state = data.source_state_uuid
      if (data.target_state_uuid) payload.target_state = data.target_state_uuid
      if (data.condition) payload.condition = data.condition
      if (data.side_effects) payload.side_effects = data.side_effects
      return api
        .post(`/workflows/admin/definitions/${workflowUuid}/actions/`, payload)
        .then((r) => r.data)
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['claim-workflows'] })
      qc.invalidateQueries({ queryKey: ['claim-workflow', vars.workflowUuid] })
    },
  })
}

export function useUpdateWorkflowAction() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: {
      workflowUuid: string
      actionUuid: string
      data: Record<string, unknown>
    }) => {
      const { actionUuid, data } = vars
      const payload: Record<string, unknown> = {}
      for (const key of ['label', 'icon', 'applicability', 'actor', 'order']) {
        if (key in data) payload[key] = data[key]
      }
      if ('source_state_uuid' in data) payload.source_state = data.source_state_uuid
      if ('target_state_uuid' in data) payload.target_state = data.target_state_uuid
      if ('condition' in data) payload.condition = data.condition
      if ('side_effects' in data) payload.side_effects = data.side_effects
      if ('ui_hints' in data) payload.ui_hints = data.ui_hints
      return api
        .patch(`/workflows/admin/actions/${actionUuid}/`, payload)
        .then((r) => r.data)
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['claim-workflows'] })
      qc.invalidateQueries({ queryKey: ['claim-workflow', vars.workflowUuid] })
    },
  })
}

export function useDeleteWorkflowAction() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: { workflowUuid: string; actionUuid: string }) =>
      api.delete(`/workflows/admin/actions/${vars.actionUuid}/`),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['claim-workflows'] })
      qc.invalidateQueries({ queryKey: ['claim-workflow', vars.workflowUuid] })
    },
  })
}

// ── Fault Definition Types ─────────────────────────────

export interface FaultDefinitionRow {
  uuid: string
  key: string
  label: string
  description: string
  category: string
  packet_document_uuid: string | null
  packet_document_filename: string | null
  checklist_document_uuid: string | null
  checklist_document_filename: string | null
  order: number
  is_active: boolean
  created_at: string
  updated_at: string
}

// ── Fault Definition Hooks ─────────────────────────────
//
// Fault definitions were deleted in Phase 1 of the tenant-agnostic
// claims migration. Phase 3 rebuilds them as a Tier 2 `fault` entity.
// Until then these hooks return an empty list / no-op mutations so
// the admin UI renders gracefully.

const FAULT_DEFINITIONS_UNAVAILABLE =
  'Fault definitions are temporarily unavailable. Phase 3 of the ' +
  'tenant-agnostic claims migration rebuilds them as a Tier 2 entity.'

export function useFaultDefinitions(workflowUuid: string | null) {
  return useQuery<FaultDefinitionRow[]>({
    queryKey: ['fault-definitions', workflowUuid],
    queryFn: async () => [],
    enabled: !!workflowUuid,
  })
}

export function useCreateFaultDefinition() {
  return useMutation({
    mutationFn: async (
      vars: { workflowUuid: string; data: Record<string, unknown> },
    ) => {
      void vars
      throw new Error(FAULT_DEFINITIONS_UNAVAILABLE)
    },
  })
}

export function useUpdateFaultDefinition() {
  return useMutation({
    mutationFn: async (vars: {
      workflowUuid: string
      faultUuid: string
      data: Record<string, unknown>
    }) => {
      void vars
      throw new Error(FAULT_DEFINITIONS_UNAVAILABLE)
    },
  })
}

export function useDeleteFaultDefinition() {
  return useMutation({
    mutationFn: async (vars: { workflowUuid: string; faultUuid: string }) => {
      void vars
      throw new Error(FAULT_DEFINITIONS_UNAVAILABLE)
    },
  })
}
